import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "@/config";
import { Stars } from "@/components/Stars";
import { Quote } from "lucide-react";

const Skeleton = () => (
  <div className="min-w-[320px] md:min-w-[380px] max-w-[420px] border border-[#EAEAEA] bg-white p-8 animate-pulse">
    <div className="h-3 w-20 bg-[#F3F4F6] mb-6" />
    <div className="h-4 w-full bg-[#F3F4F6] mb-2" />
    <div className="h-4 w-11/12 bg-[#F3F4F6] mb-2" />
    <div className="h-4 w-8/12 bg-[#F3F4F6]" />
    <div className="h-3 w-32 bg-[#F3F4F6] mt-8" />
  </div>
);

const ReviewCard = ({ r, idx }) => {
  const date = r.created_at ? new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short" }) : "";
  return (
    <article
      data-testid={`review-card-${idx}`}
      className="group relative min-w-[320px] md:min-w-[380px] max-w-[420px] border border-[#EAEAEA] bg-white p-8 md:p-9 flex flex-col transition-colors hover:border-[#0A192F]"
    >
      <Quote className="text-[#0A192F]/15 absolute right-6 top-6" size={40} strokeWidth={1.25} />
      <Stars value={r.rating || 5} />
      <p className="mt-6 text-[15px] leading-relaxed text-[#0A0A0B] font-body flex-1">
        &ldquo;{r.content}&rdquo;
      </p>
      <div className="mt-8 pt-6 border-t border-[#EAEAEA]">
        <div className="flex items-baseline justify-between gap-4">
          <div className="font-display font-medium text-[#0A0A0B]">{r.name}</div>
          {date && (
            <div className="text-xs uppercase tracking-[0.18em] text-[#4A4A52]">
              {date}
            </div>
          )}
        </div>
        {r.role && (
          <span
            data-testid={`review-role-${idx}`}
            className="inline-block mt-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A192F] bg-[#E2E6EE] px-2.5 py-1.5 border border-[#0A192F]/10"
          >
            {r.role}
          </span>
        )}
      </div>
    </article>
  );
};

export const Reviews = () => {
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/reviews`);
        setReviews(data);
      } catch {
        setReviews([]);
      }
    })();
  }, []);

  const empty = reviews && reviews.length === 0;

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="border-b border-[#EAEAEA] bg-[#FCFCFC]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-2xl mb-10 md:mb-14">
          <div className="overline">What clients say</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium mt-4 leading-[1.05]">
            Reviews from parents, students &amp; schools.
          </h2>
          <p className="mt-5 text-[#4A4A52] text-base md:text-lg leading-relaxed">
            A selection of feedback from families, students, and educators I&apos;ve worked with.
          </p>
        </div>

        {reviews === null ? (
          <div className="flex gap-6 overflow-hidden">
            <Skeleton /><Skeleton /><Skeleton />
          </div>
        ) : empty ? (
          <div
            data-testid="reviews-empty"
            className="border border-dashed border-[#EAEAEA] bg-white p-12 md:p-16 text-center"
          >
            <Quote className="mx-auto text-[#0A192F]/20" size={40} strokeWidth={1.25} />
            <h3 className="font-display text-2xl font-medium mt-4">Reviews will appear here soon.</h3>
            <p className="mt-3 text-[#4A4A52] max-w-md mx-auto">
              New testimonials are added after each engagement.
            </p>
          </div>
        ) : (
          <div
            data-testid="reviews-list"
            className="flex gap-6 overflow-x-auto pb-4 -mx-6 px-6 md:mx-0 md:px-0 snap-x snap-mandatory"
            style={{ scrollbarWidth: "thin" }}
          >
            {reviews.map((r, i) => (
              <div key={r.id} className="snap-start">
                <ReviewCard r={r} idx={i} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
